/**
 * Voo, colisão e stamina (GDD, seções 2 e 4; item 4.1). Distâncias em metros, tempo em segundos.
 * Os atributos (0 a 99) escolhem um valor entre `min` e `max` de cada faixa.
 */
import { ATHLETE_VIEW_SCALE } from "./pitch.js";
import { STATS, type Stat } from "./athlete.js";

export interface Range {
  min: number;
  max: number;
}

export const FLIGHT = {
  /**
   * Raio de colisão do atleta com a vassoura. Os atletas aparecem 2,45× maiores no estádio
   * (ATHLETE_VIEW_SCALE), então a colisão acompanha o tamanho que o jogador vê.
   */
  athleteRadius: 0.5 * ATHLETE_VIEW_SCALE,
  /** Altura mínima da vassoura: com a escala de exibição, os pés do atleta ficam logo acima da grama. */
  minHeight: 2.6,
  /** Velocidade máxima de cruzeiro, pelo atributo Velocidade. */
  maxSpeed: { min: 13, max: 19 },
  /** Aceleração para a frente, pelo atributo Aceleração. */
  acceleration: { min: 9, max: 17 },
  /** Aceleração lateral (curva), pelo atributo Controle. */
  turnAcceleration: { min: 14, max: 26 },
  /**
   * Quanto a vassoura gira por segundo (rad/s), pelo atributo Controle. A direção nunca salta: uma
   * meia-volta leva cerca de 1 s, freando e girando ao mesmo tempo.
   */
  turnRate: { min: 2.6, max: 4.2 },
  /** Frenagem quando o jogador solta o direcional ou pede o sentido oposto. */
  brakeAcceleration: 20,
  climbSpeed: 8,
  climbAcceleration: 16,
  turboSpeedFactor: 1.45,
  turboAccelerationFactor: 1.6,
  /** Fração da stamina gasta por segundo de turbo, pelo atributo Stamina (mais stamina gasta menos). */
  staminaDrain: { min: 0.34, max: 0.2 },
  /** Fração recuperada por segundo sem turbo. */
  staminaRegen: { min: 0.07, max: 0.14 },
  /** Depois de esgotar, o turbo só volta quando a stamina chega aqui. */
  staminaRecoverAt: 0.25,
  /** Quanto da velocidade de aproximação sobra depois de um choque (0 = gruda, 1 = quica). */
  restitution: 0.35,
  /** Massa relativa pelo atributo Estabilidade: o atleta estável é empurrado menos. */
  mass: { min: 0.85, max: 1.25 },
  /** Postes e aros dos gols, para colisão. */
  hoopPoleRadius: 0.45,
  hoopTubeRadius: 0.32,
  /** Valor de atributo usado quando o atleta ainda não tem ficha (bots e testes). */
  defaultStat: 70,
} as const;

/** Atributos que mexem no voo (os outros valem para as bolas). */
export const FLIGHT_STATS = [
  "velocidade",
  "aceleracao",
  "controle",
  "stamina",
  "estabilidade",
] as const satisfies readonly Stat[];
/** Valor atual de cada atributo do atleta na partida (0 a 99). */
export type FlightStats = Record<Stat, number>;

export function defaultFlightStats(): FlightStats {
  const stats = {} as FlightStats;
  for (const s of STATS) stats[s] = FLIGHT.defaultStat;
  return stats;
}

/** Valor da faixa para um atributo de 0 a 99. */
export function byStat(range: Range, stat: number): number {
  const t = Math.max(0, Math.min(99, stat)) / 99;
  return range.min + (range.max - range.min) * t;
}
